import { fetchJSONWithTimeout } from '@noths/polaris-client-utils';

import { PUBLIC_API_BASE_PATH } from 'src/constants/environment';
import type { ReviewsPage, ReviewsPageResponse } from './types';

export const REVIEWS_API_PATH = `${PUBLIC_API_BASE_PATH}reviews`;

/**
 * Longer than the route's own 3s budget against Feefo, so an answer just inside that limit
 * is not cut off here.
 */
const CLIENT_TIMEOUT_MS = 6000;

/**
 * One retry only. Feefo finishes computing a cold query even when the request is aborted
 * and caches the result, so the first attempt mostly starts the clock and the retry collects
 * the warm answer for the common case. Cold queries have been measured as slow as ~50s,
 * but reviews are non-critical: past ~11s the error state, and the next customer, collect it.
 */
export const RETRY_DELAYS_MS = [5000];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface Attempt {
  payload?: ReviewsPage;
  /** Whether asking again could plausibly give a different answer. */
  retryable: boolean;
}

const hasPage = (payload: ReviewsPageResponse): payload is ReviewsPage => payload.page !== null;

const attemptFetch = async (url: string): Promise<Attempt> => {
  try {
    const response = await fetchJSONWithTimeout(url, { timeout: CLIENT_TIMEOUT_MS });

    if (response.ok) {
      const payload: ReviewsPageResponse = await response.json();

      // A null page is the route saying its own Feefo call failed or timed out: the
      // cold-Feefo case the delays above exist for, served as a 200 so it does not count as
      // an error of ours.
      return hasPage(payload) ? { payload, retryable: false } : { retryable: true };
    }

    // A 5xx never comes from the route's own Feefo handling now, but one from in front of it is
    // still worth retrying; nothing else is.
    return { retryable: response.status >= 500 };
  } catch {
    // A timeout or network failure: the cold-Feefo case the delays above exist for.
    return { retryable: true };
  }
};

export interface FetchProductReviewsClientOptions {
  page?: number;
  /** Star rating to filter by, 1 to 5. Omit for all ratings. */
  rating?: number | null;
}

/**
 * A page of reviews from our own API route, retried through a cold upstream. Resolves
 * null once the retries are exhausted. An OK response with no reviews is returned as-is:
 * a product with nothing to show is an answer, not a failure.
 */
export const fetchProductReviewsClient = async (
  productCode: number,
  { page, rating }: FetchProductReviewsClientOptions = {},
): Promise<ReviewsPage | null> => {
  const params = new URLSearchParams();

  if (page) {
    params.set('page', String(page));
  }

  if (rating) {
    params.set('rating', String(rating));
  }

  const query = params.toString();
  const url = `${REVIEWS_API_PATH}/${productCode}${query ? `?${query}` : ''}`;

  for (let attempt = 0; ; attempt += 1) {
    const { payload, retryable } = await attemptFetch(url);

    if (payload) {
      return payload;
    }

    if (!retryable || attempt >= RETRY_DELAYS_MS.length) {
      return null;
    }

    await wait(RETRY_DELAYS_MS[attempt]);
  }
};
